import { BathingPlace, BathingPlaceSource } from '../types';
import { getCache, getCacheStale, setCache, CACHE_TTL } from './cacheService';
import { robustOverpassQuery } from './overpassService';
import { db } from './firebaseService';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';

// Norge dekningsområde (fra countryDetection.ts)
const NORWAY_BBOX = '57.9,4.5,71.2,31.1';

const STALE_THRESHOLD_MS = 5 * 24 * 60 * 60 * 1000; // 5 dager

/**
 * Sjekker om en temperaturmåling er for gammel til å vises.
 * Yr leverer ikke data eldre enn 5 dager.
 * Manglende tidsstempel (HaV on-demand) regnes som ferskt.
 */
export function isTemperatureStale(temperatureTime?: string): boolean {
  if (!temperatureTime) return false;
  const measureTime = new Date(temperatureTime).getTime();
  if (isNaN(measureTime)) return false;
  return (Date.now() - measureTime) > STALE_THRESHOLD_MS;
}

const PROXY_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy';
const HAV_FEATURE_URL = 'https://badplatsen.havochvatten.se/badplatsen/api/feature/';
const HAV_PROFILE_URL = 'https://badplatsen.havochvatten.se/badplatsen/api/testlocationprofile/';

// ─── Sweden: HaV Badplatsen API ───────────────────────────────────

export const fetchSwedishBathingPlaces = async (): Promise<BathingPlace[]> => {
  const cacheKey = 'bathing_hav_all_v2';
  const { data: staleData, isStale } = getCacheStale(cacheKey);
  if (staleData && !isStale) return staleData;

  try {
    const res = await fetch(`${PROXY_URL}?url=${encodeURIComponent(HAV_FEATURE_URL)}`);
    if (!res.ok) throw new Error(`HaV API ${res.status}`);
    const geojson = await res.json();

    const features = geojson?.features || [];
    const result: BathingPlace[] = features
      .map((f: any) => {
        const coords = f.geometry?.coordinates;
        if (!coords || coords.length < 2) return null;
        const props = f.properties || {};
        return {
          id: `hav-${f.id || props.NUTSKOD || `${coords[0]}-${coords[1]}`}`,
          source: 'hav' as const,
          name: props.NAMN || 'Badplats',
          location: { latitude: coords[1], longitude: coords[0] },
          municipality: props.KMN_NAMN,
          profileId: props.NUTSKOD || f.id,
        };
      })
      .filter(Boolean) as BathingPlace[];

    setCache(cacheKey, result, CACHE_TTL.LONG);
    return result;
  } catch (e) {
    console.error('Failed to fetch Swedish bathing places:', e);
    return staleData || [];
  }
};

export const fetchSwedishBathingProfile = async (profileId: string): Promise<Partial<BathingPlace>> => {
  const cacheKey = `bathing_hav_profile_${profileId}`;
  const cached = getCache(cacheKey);
  if (cached) return cached;

  try {
    const url = `${HAV_PROFILE_URL}${encodeURIComponent(profileId)}`;
    const res = await fetch(`${PROXY_URL}?url=${encodeURIComponent(url)}`);
    if (!res.ok) throw new Error(`HaV profile ${res.status}`);
    const data = await res.json();

    const result: Partial<BathingPlace> = {};

    // Water quality warnings
    if (data.algblomning || data.cyano) {
      result.algalBloom = true;
      result.waterQualityWarning = true;
      result.warningText = 'Algblomning';
    }
    if (data.badförbud || data.avradanFranBad) {
      result.bathingRestriction = true;
      result.waterQualityWarning = true;
      result.warningText = result.warningText
        ? `${result.warningText}, Avrådan från bad`
        : 'Avrådan från bad';
    }

    // Temperature
    if (data.temperatur != null && data.temperatur > 0) {
      result.waterTemperature = data.temperatur;
    }

    setCache(cacheKey, result, CACHE_TTL.LONG);
    return result;
  } catch (e) {
    console.warn('Failed to fetch HaV profile:', e);
    return {};
  }
};

// ─── Viewport-based OSM Overpass (Norway, Denmark, etc.) ────────

const parseOverpassBathingResults = (data: any): BathingPlace[] =>
  (data.elements || [])
    .map((e: any) => {
      if (!e.lat || !e.lon) return null;
      const tags = e.tags || {};
      return {
        id: `osm-bath-${e.id}`,
        source: 'osm' as const,
        name: tags.name || (tags.leisure === 'bathing_place' ? 'Badeplass' : tags.natural === 'beach' ? 'Strand' : 'Badeområde'),
        location: { latitude: e.lat, longitude: e.lon },
        osmType: tags.leisure || tags.natural,
        website: tags.website,
        description: tags.description || tags.note,
      };
    })
    .filter(Boolean) as BathingPlace[];

/**
 * Henter ALLE norske badeplasser. Sjekker i rekkefølge:
 * 1. Lokal cache (localStorage, 4 timer)
 * 2. Firestore persistent cache (30 dager)
 * 3. Overpass API (full Norge-query, lagrer i Firestore for alle brukere)
 *
 * Returnerer hele datasettet — filtrering skjer i hook/komponent.
 */
export const fetchAllNorwegianBathingPlaces = async (signal?: AbortSignal): Promise<BathingPlace[]> => {
  const localCacheKey = 'bathing_osm_norway_all_v1';
  const { data: staleData, isStale } = getCacheStale(localCacheKey);
  if (staleData && !isStale) return staleData;

  // 1. Sjekk Firestore persistent cache
  try {
    const docRef = doc(db, 'osm_bathing_cache', 'norway_all');
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const results = snap.data().results as BathingPlace[];
      setCache(localCacheKey, results, CACHE_TTL.LONG);
      console.log(`Bathing: loaded ${results.length} Norwegian places from Firestore cache`);
      return results;
    }
  } catch (fsErr) {
    console.warn('Firestore bathing cache read failed, falling back to Overpass:', fsErr);
  }

  // 2. Hent fra Overpass — hele Norge i én spørring
  const query = `[out:json][timeout:60];(node["leisure"="bathing_place"](${NORWAY_BBOX});node["natural"="beach"](${NORWAY_BBOX});node["leisure"="swimming_area"](${NORWAY_BBOX}););out body;`;

  try {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const data = await robustOverpassQuery(query, { lastQuery: '', lastUrl: '', lastResponse: null, lastError: null }, signal);
    const result = parseOverpassBathingResults(data);
    console.log(`Bathing: fetched ${result.length} Norwegian places from Overpass, saving to Firestore`);

    // 3. Lagre lokalt
    setCache(localCacheKey, result, CACHE_TTL.LONG);

    // 4. Lagre i Firestore for alle brukere (fire-and-forget)
    try {
      const docRef = doc(db, 'osm_bathing_cache', 'norway_all');
      setDoc(docRef, {
        results: result,
        fetchedAt: Timestamp.now(),
        count: result.length,
      }, { merge: true }).catch(() => {});
    } catch { /* ignorér Firestore-skrivefeil */ }

    return result;
  } catch (e: any) {
    if (e.name !== 'AbortError') {
      console.error('Failed to fetch Norwegian bathing places:', e);
    }
    return staleData || [];
  }
};

// ─── Viewport-based bathing places (for any country via Overpass) ──

import { snapBboxToGrid } from '../utils/boundsUtils';
import type { MapBounds } from '../types';

export const fetchBathingPlacesInBounds = async (
  bounds: MapBounds,
  signal?: AbortSignal
): Promise<BathingPlace[]> => {
  const bboxKey = snapBboxToGrid(bounds, 0.05);
  const cacheKey = `bathing_osm_bounds_v1_${bboxKey}`;
  const { data: staleData, isStale } = getCacheStale(cacheKey);
  if (staleData && !isStale) return staleData;

  const bbox = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const query = `[out:json][timeout:25];(node["leisure"="bathing_place"](${bbox});node["natural"="beach"](${bbox});node["leisure"="swimming_area"](${bbox}););out body;`;

  try {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const data = await robustOverpassQuery(query, { lastQuery: '', lastUrl: '', lastResponse: null, lastError: null }, signal);
    const result = parseOverpassBathingResults(data);
    setCache(cacheKey, result, CACHE_TTL.LONG);
    return result;
  } catch (e: any) {
    if (e.name !== 'AbortError') console.error('Bathing bounds fetch failed:', e);
    return staleData || [];
  }
};

// ─── Norway: Yr Badetemperaturer ──────────────────────────────────

const YR_PROXY_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/yrBathingTemperatures';

interface YrBathingEntry {
  latitude: number;
  longitude: number;
  temperature: number;
  name?: string;
  sourceName?: string;
  time?: string;
}

/**
 * Henter alle norske badetemperaturer via Cloud Function proxy.
 * Caches lokalt i 1 time.
 */
export const fetchYrBathingTemperatures = async (): Promise<YrBathingEntry[]> => {
  const cacheKey = 'yr_bathing_temps_v1';
  const cached = getCache(cacheKey);
  if (cached) return cached;

  try {
    const res = await fetch(YR_PROXY_URL);
    if (!res.ok) return [];
    const data = await res.json();

    // Yr returnerer enten array direkte eller { entries: [...] }
    const entries: YrBathingEntry[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.entries) ? data.entries
      : Array.isArray(data?.data) ? data.data
      : [];

    // Filtrer til entries med gyldig posisjon og temperatur
    const valid = entries.filter(e =>
      e.latitude != null && e.longitude != null &&
      e.temperature != null && e.temperature > 0
    );

    setCache(cacheKey, valid, CACHE_TTL.LONG);
    return valid;
  } catch (e) {
    console.warn('Failed to fetch Yr bathing temperatures:', e);
    return [];
  }
};

/**
 * Matcher Yr-temperaturer til eksisterende badeplasser (~3km terskel)
 * og oppretter nye BathingPlace-objekter for umatchede Yr-stasjoner.
 *
 * - Muterer eksisterende plasser (waterTemperature + temperatureTime)
 * - Returnerer array med nye BathingPlace for umatchede Yr-stasjoner
 */
export function mergeYrTemperatures(places: BathingPlace[], yrData: YrBathingEntry[]): BathingPlace[] {
  if (!yrData.length) return [];

  const MATCH_THRESHOLD = 0.015; // ~1.5km
  const matchedYrIndices = new Set<number>();

  // Pass 1: Knytt temperaturer til eksisterende plasser
  for (const place of places) {
    if (place.waterTemperature) continue;

    let bestDist = MATCH_THRESHOLD;
    let bestIdx = -1;

    for (let i = 0; i < yrData.length; i++) {
      const yr = yrData[i];
      const latDiff = Math.abs(place.location.latitude - yr.latitude);
      const lonDiff = Math.abs(place.location.longitude - yr.longitude);
      const dist = Math.sqrt(latDiff * latDiff + lonDiff * lonDiff);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }

    if (bestIdx >= 0) {
      place.waterTemperature = yrData[bestIdx].temperature;
      place.temperatureTime = yrData[bestIdx].time;
      matchedYrIndices.add(bestIdx);
    }
  }

  // Pass 2: Opprett nye badeplasser for umatchede Yr-stasjoner
  const newPlaces: BathingPlace[] = [];
  for (let i = 0; i < yrData.length; i++) {
    if (matchedYrIndices.has(i)) continue;
    const yr = yrData[i];
    newPlaces.push({
      id: `yr-${yr.latitude.toFixed(4)}-${yr.longitude.toFixed(4)}`,
      source: 'yr',
      name: yr.name || 'Badetemperatur',
      location: { latitude: yr.latitude, longitude: yr.longitude },
      waterTemperature: yr.temperature,
      temperatureTime: yr.time,
    });
  }

  return newPlaces;
}

// ─── Deduplication ────────────────────────────────────────────────

const SOURCE_PRIORITY: Record<BathingPlaceSource, number> = { hav: 0, osm: 1, yr: 2 };

export function deduplicateBathingPlaces(points: BathingPlace[]): BathingPlace[] {
  const THRESHOLD = 0.0005; // ~50m
  const result: BathingPlace[] = [];
  const used = new Set<number>();

  // Prioritet: hav > osm > yr
  points.sort((a, b) => SOURCE_PRIORITY[a.source] - SOURCE_PRIORITY[b.source]);

  for (let i = 0; i < points.length; i++) {
    if (used.has(i)) continue;
    const winner = points[i];
    result.push(winner);
    used.add(i);

    for (let j = i + 1; j < points.length; j++) {
      if (used.has(j)) continue;
      const latDiff = Math.abs(winner.location.latitude - points[j].location.latitude);
      const lonDiff = Math.abs(winner.location.longitude - points[j].location.longitude);
      if (latDiff < THRESHOLD && lonDiff < THRESHOLD) {
        // Arv temperatur fra duplikaten hvis vinneren mangler
        if (!winner.waterTemperature && points[j].waterTemperature) {
          winner.waterTemperature = points[j].waterTemperature;
          winner.temperatureTime = points[j].temperatureTime;
        }
        used.add(j);
      }
    }
  }
  return result;
}
